import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, FileSpreadsheet, Filter, Download, FileText } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import PageGuide from '../components/PageGuide';

const POINTS = [
  { icon: FileSpreadsheet, title: 'Real Excel (.xlsx) & CSV', desc: 'Export a proper Excel workbook or a universal CSV — open it in Excel, Google Sheets, Numbers or import into any accounting tool. Receipt exports include two linked sheets — Invoices and Line Items — connected by the MF number.' },
  { icon: Filter, title: 'Filter before you export', desc: 'Narrow by date range, income vs expense, or category so your accountant gets exactly the rows they need.' },
  { icon: FileText, title: 'Tax-ready columns', desc: 'Every row includes date, amount, currency, category, vendor, description and the VAT/tax line — clean and consistent.' },
  { icon: Download, title: 'One-click download', desc: 'No copy-paste, no cleanup. Hit export and hand the file straight to your bookkeeper or CPA.' },
];

const FAQS = [
  { q: 'How do I export my expenses to Excel?', a: 'Open the Export Center in MoneyFeed, choose your date range and filters, and click "Download Excel". You get a ready-to-use .xlsx workbook of all your transactions.' },
  { q: 'Can I export to CSV instead?', a: 'Yes. CSV is available alongside Excel, so you can import your data into any spreadsheet or accounting software.' },
  { q: 'Can I export just one category or date range?', a: 'Yes. Filter by date range, income/expense type and category before exporting, so the file contains exactly what you need.' },
];

export default function ExportToExcel() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'Export Expenses to Excel & CSV', url: 'https://moneyfeed.io/export-to-excel', description: 'Export your business expenses and income to Excel (.xlsx) or CSV, filtered by date, type and category — accountant-ready in one click.' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="export-to-excel-page">
      <Seo
        title="Export Expenses to Excel & CSV — Accountant-Ready | MoneyFeed"
        description="Export your income and expenses to a real Excel (.xlsx) or CSV in one click — filtered by date, type and category, and ready for QuickBooks, Xero or your accountant. No spreadsheets to build."
        path="/export-to-excel"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <section className="max-w-5xl mx-auto px-4 sm:px-8 pt-14 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#059669]/10 rounded-full mb-5">
          <FileSpreadsheet className="h-4 w-4 text-[#059669]" />
          <span className="text-xs font-bold text-[#059669] uppercase tracking-wider">Export Center</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">
          Export your expenses to <span className="text-[#059669]">Excel & CSV</span>
        </h1>
        <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
          Turn your MoneyFeed transactions into clean, accountant-ready spreadsheets. Filter by date, type or category and download a real Excel workbook or CSV in one click.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/auth">
            <Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="export-get-started">
              Start free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <Link to="/invoice-scanner">
            <Button variant="outline" className="w-full sm:w-auto border-[#064E3B]/30 text-[#064E3B] hover:bg-[#064E3B]/5 font-bold rounded-full px-7 py-6 text-base" data-testid="export-scanner-link">
              Scan invoices too
            </Button>
          </Link>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {POINTS.map((p, i) => (
            <div key={p.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`export-point-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center mb-4">
                <p.icon className="h-5 w-5 text-[#059669]" />
              </div>
              <h3 className="font-bold text-[#064E3B] mb-1">{p.title}</h3>
              <p className="text-gray-600 text-sm">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <PageGuide />

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Export FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`export-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

            <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-8"><Link to="/scan-center" className="block bg-white rounded-2xl shadow-sm border border-gray-100 p-6 text-center hover:shadow-md transition"><p className="text-sm font-semibold text-[#064E3B]">Do this automatically for every receipt — meet MoneyFeed Scan Center</p><p className="text-gray-600 text-sm mt-2">Snap or send receipts on WhatsApp or in the app and MoneyFeed reads, categorizes and files them into clean books.</p><span className="inline-block mt-3 text-sm font-semibold text-[#0D9488]">Explore Scan Center →</span></Link></section>
      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/business" className="hover:text-[#064E3B]">For Business</Link>
          <Link to="/invoice-scanner" className="hover:text-[#064E3B]">Invoice Scanner</Link>
          <Link to="/for-accountants" className="hover:text-[#064E3B]">For Accountants</Link>
        </div>
      </footer>
    </div>
  );
}
